import React, { useState, useEffect, useRef } from 'react';
import { Tabs as PloneTabs } from '@plone/components';
import { clsx } from 'clsx';
import './Tabs.scss';

export interface ESSTabsProps {
  variant?: 'default' | 'pills' | 'underline';
  className?: string;
  children?: React.ReactNode;
  dropdownBreakpoint?: number; // Viewport width in pixels to switch to dropdown mode
  dropdownLabel?: string; // Label to show above dropdown
  defaultValue?: string; // Default selected tab
  onValueChange?: (value: string) => void; // Callback when tab changes
  [key: string]: any; // Allow any props from @plone/components Tabs
}

interface TabItem {
  value: string;
  label: string;
  content: React.ReactNode;
}

/**
 * ESS Tabs Component
 *
 * A tabbed interface component for organizing content into sections.
 * Wraps @plone/components Tabs with ESS design system styling.
 *
 * Features:
 * - Responsive dropdown mode: Automatically switches to dropdown when viewport width is below breakpoint
 * - Custom dropdown label: Display custom text above dropdown
 * - Real dropdown: Shows selected tab as button, expands to show all options on click
 *
 * Note: This component wraps @plone/components Tabs in a styled div.
 *
 * @example
 * ```tsx
 * import { ESSTabs } from './components/Molecules/Tabs';
 *
 * // Standard tabs
 * <ESSTabs variant="underline">
 *   // Tab content goes here - check @plone/components docs for exact API
 * </ESSTabs>
 *
 * // Tabs with dropdown mode at 768px viewport width
 * <ESSTabs 
 *   variant="underline"
 *   dropdownBreakpoint={768}
 *   dropdownLabel="Select a category to explore:"
 * >
 *   // Tab content goes here
 * </ESSTabs>
 * ```
 */
export const ESSTabs: React.FC<ESSTabsProps> = ({
  variant = 'default',
  className,
  children,
  dropdownBreakpoint,
  dropdownLabel = 'Select a category:',
  defaultValue,
  onValueChange,
  ...props
}) => {
  const [isDropdownMode, setIsDropdownMode] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(defaultValue || '');
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  // Extract tab information from children
  useEffect(() => {
    if (!tabsContainerRef.current) return;

    const extractTabs = () => {
      const tablist = tabsContainerRef.current?.querySelector('[role="tablist"]');
      const tabpanels = tabsContainerRef.current?.querySelectorAll('[role="tabpanel"]');
      
      if (!tablist) return;

      const tabButtons = Array.from(tablist.querySelectorAll('[role="tab"]'));
      const extractedTabs: TabItem[] = tabButtons.map((button, index) => {
        const value = button.getAttribute('data-value') || button.getAttribute('id') || `tab-${index}`;
        const label = button.textContent || '';
        const content = tabpanels?.[index]?.innerHTML || '';
        
        return { value, label, content };
      });

      setTabs(extractedTabs);

      // Set initial selected tab
      const activeTab = tablist.querySelector('[role="tab"][aria-selected="true"], [role="tab"][data-selected="true"]');
      if (activeTab && !selectedTab) {
        const value = activeTab.getAttribute('data-value') || activeTab.getAttribute('id') || extractedTabs[0]?.value || '';
        setSelectedTab(value);
      }
    };

    // Wait for tabs to render
    setTimeout(extractTabs, 100);
  }, [children]);

  useEffect(() => {
    if (!dropdownBreakpoint) return;

    const checkViewportWidth = () => {
      setIsDropdownMode(window.innerWidth < dropdownBreakpoint);
    };

    // Check initial width
    checkViewportWidth();

    // Add resize listener
    window.addEventListener('resize', checkViewportWidth);

    return () => {
      window.removeEventListener('resize', checkViewportWidth);
    };
  }, [dropdownBreakpoint]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownMode || !isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownMode, isDropdownOpen]);

  // Update selected tab when tabs change in normal mode
  useEffect(() => {
    if (isDropdownMode || !tabsContainerRef.current) return;

    const tablist = tabsContainerRef.current.querySelector('[role="tablist"]');
    if (!tablist) return;

    const handleTabClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.getAttribute('role') === 'tab') {
        const value = target.getAttribute('data-value') || target.getAttribute('id') || '';
        if (value) {
          setSelectedTab(value);
          onValueChange?.(value);
        }
      }
    };

    tablist.addEventListener('click', handleTabClick);
    return () => {
      tablist.removeEventListener('click', handleTabClick);
    };
  }, [isDropdownMode, onValueChange]);

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTabSelect = (value: string) => {
    setSelectedTab(value);
    setIsDropdownOpen(false);
    onValueChange?.(value);

    // Trigger click on the actual tab in the hidden PloneTabs
    if (tabsContainerRef.current) {
      const tablist = tabsContainerRef.current.querySelector('[role="tablist"]');
      const tabButtons = tablist?.querySelectorAll('[role="tab"]');
      tabButtons?.forEach((button) => {
        const btnValue = button.getAttribute('data-value') || button.getAttribute('id');
        if (btnValue === value) {
          (button as HTMLElement).click();
        }
      });
    }
  };

  const getSelectedTabLabel = () => {
    const selected = tabs.find(tab => tab.value === selectedTab);
    return selected?.label || tabs[0]?.label || 'Select';
  };

  return (
    <div 
      ref={dropdownRef}
      className={clsx(
        'ess-tabs', 
        `ess-tabs--${variant}`, 
        {
          'ess-tabs--dropdown': isDropdownMode,
          'ess-tabs--dropdown-open': isDropdownMode && isDropdownOpen,
        },
        className
      )}
    >
      {isDropdownMode ? (
        <>
          {dropdownLabel && (
            <div className="ess-tabs__dropdown-label">{dropdownLabel}</div>
          )}
          <div className="ess-tabs__dropdown-container">
            <button
              type="button"
              className="ess-tabs__dropdown-button"
              onClick={handleDropdownToggle}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
            >
              <span className="ess-tabs__dropdown-button-text">{getSelectedTabLabel()}</span>
              <span className="ess-tabs__dropdown-button-icon">
                {isDropdownOpen ? '▲' : '▼'}
              </span>
            </button>
            {isDropdownOpen && (
              <div className="ess-tabs__dropdown-menu" role="menu">
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    className={clsx(
                      'ess-tabs__dropdown-item',
                      { 'ess-tabs__dropdown-item--selected': tab.value === selectedTab }
                    )}
                    onClick={() => handleTabSelect(tab.value)}
                    role="menuitem"
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
      
      {/* Hidden PloneTabs - always rendered but hidden in dropdown mode */}
      <div 
        ref={tabsContainerRef}
        className={clsx('ess-tabs__plone-wrapper', {
          'ess-tabs__plone-wrapper--hidden': isDropdownMode
        })}
      >
        <PloneTabs {...props}>{children}</PloneTabs>
      </div>
    </div>
  );
};

export default ESSTabs;
